import React, { useRef } from 'react'

import {
  View,
  StyleSheet,
  KeyboardAvoidingView,
  ScrollView
} from 'react-native'

import isNumber from 'lodash/isNumber'
import client, {useData} from '@sart/client'

import {
  Description,
  Text,
  Label,
  Space,
  MoonDial,
  TabSelector,
  Slider,
  TextInputExtended,
  Button,
  ErrorMessage
} from '@sart/components'
import {
  ChevronRight
} from '@sart/icons'
import {
  adaptive,
  colors,
  typography
} from '@sart/styles'
import { useClient } from '@sart/client'
import { useContent } from '@sart/content'
import { testID } from '@sart/utilities'

import {
  parseTextToValue,
  minMaxLimits,
  balanceCalculator,
  transformAtoB,
  getTransferableBalances,
  parseTextToSlideValue
} from './transferUtils'
import { Platform } from 'react-native'

const phoneNumber = '07578432255'

export default function Transform(props) {
  const content = useContent()

  const { navigation } = props

  const [{ loading, error }, setData] = useData({ loading: false})

  const {
    cache: {
      accounts,
      balances,
    } = {}
  } = useClient()

  // Slider (amount transformed)
  const [slide, setSlide] = React.useState(0)

  // Tabs Selection
  const [typeA, setTypeA] = React.useState('TEXT')
  const [typeB, setTypeB] = React.useState('DATA')

  // Tab Options
  const tabs = [
    { value: 'TEXT', label: content.swap.texts },
    { value: 'DATA', label: content.swap.data },
    { value: 'MINS', label: content.swap.minutes },
  ]
  // Only certain balances can actually be transferred or transformed
  const transformableBalances = getTransferableBalances(balances)

  const [balanceTransformProducts, setBalanceTransformProducts] = React.useState([])

  const [dataToTextRate, setDataToTextRate] = React.useState(1)
  const [dataToMinsRate, setDataToMinsRate] = React.useState(1)
  const [minsToDataRate, setMinsToDataRate] = React.useState(1)
  const [minsToTextRate, setMinsToTextRate] = React.useState(1)
  const [textToDataRate, setTextToDataRate] = React.useState(1)
  const [textToMinsRate, setTextToMinsRate] = React.useState(1)

  function getConversionRate(typeA, typeB) {
    if(typeA === "DATA" && typeB === "TEXT") {
      return dataToTextRate
    }
     else if(typeA === "DATA" && typeB === "MINS") {
      return dataToMinsRate
    }
    else if(typeA === "MINS" && typeB === "DATA") {
      return minsToDataRate
    }
    else if(typeA === "MINS" && typeB === "TEXT") {
      return minsToTextRate
    }
    else if(typeA === "TEXT" && typeB === "DATA") {
      return textToDataRate
    }
    else if(typeA === "TEXT" && typeB === "MINS") {
      return textToMinsRate
    }
  }
  // Dial values
  const balanceA = React.useMemo(() => handleBalanceACalculation(slide), [transformableBalances, typeA, slide])
  const balanceB = React.useMemo(() => handleBalanceBCalculation(slide), [transformableBalances, typeA, typeB, slide])

  // TextInput values
  const [textA, setTextA] = React.useState(balanceA ? balanceA.formatValue(balanceA.min) : 0)
  const [textB, setTextB] = React.useState(balanceB ? balanceB.formatValue(balanceB.min) : 0)

  // Validation
  const isValid = slide >= (balanceA && balanceA.min) && slide <= (balanceA && balanceA.max)
  const haveTransformableText = transformableBalances && transformableBalances.text && transformableBalances.text.quantity > 0
  const haveTransformableData = transformableBalances && transformableBalances.data && transformableBalances.data.quantity > 0
  const haveTransformableVoice = transformableBalances && transformableBalances.voice && transformableBalances.voice.quantity > 0
  const noTransferableBalances = !haveTransformableText && !haveTransformableData && !haveTransformableVoice

  function hasAvailableBalance(tab){
    if (tab=='TEXT') {
      if(transformableBalances && transformableBalances.text && transformableBalances.text.quantity < 5) {
      return true
      } 
    }
    if (tab=='DATA') {
      if (transformableBalances && transformableBalances.data && transformableBalances.data.quantity < 5 * 1000) {
        return true
      }
    }
    if (tab=='MINS') {
      if (transformableBalances && transformableBalances.voice && transformableBalances.voice.quantity < 5 * 60) {
        return true
      }
    }
  }
  // Messages
  const result = noTransferableBalances ? content.swap.noApplicableBalances : `${content.swap.transform} ${textA} ${handleSuffixTranslation(balanceA)} ${content.swap.into} ${textB} ${handleSuffixTranslation(balanceB)}`
  const limits = noTransferableBalances ? content.swap.noApplicableBalances : `${content.swap.minimum} ${balanceA.formatValue(balanceA.min)} ${handleSuffixTranslation(balanceA)}, ${content.swap.maximum} ${balanceA.formatValue(balanceA.max)} ${handleSuffixTranslation(balanceA)}`
  
  React.useEffect(() => {
    // Initiate all on minimum
    setSlide(balanceA ? balanceA.min : 0)
    setTextA(balanceA ? balanceA.formatValue(balanceA.min) : 0)
    setTextB(balanceB ? balanceB.formatValue(balanceB.min) : 0)
  }, [typeA, typeB])

  React.useEffect(() =>  { getTransformRates() },[])

  async function getTransformRates() {
    try {
      const transformProducts = await client.callApi('catalog','getBalanceTransformProducts',[client.cache.catalogId])
      setBalanceTransformProducts(transformProducts)

      setDataToTextRate(transformProducts.find((product) => product.productLegacyId === `BALANCE-TRANSFORM-DATA-TO-TEXT-CMP`).converstionRate)
      setDataToMinsRate(transformProducts.find((product) => product.productLegacyId === `BALANCE-TRANSFORM-DATA-TO-MINS-CMP`).converstionRate)
      setMinsToDataRate(transformProducts.find((product) => product.productLegacyId === `BALANCE-TRANSFORM-MINS-TO-DATA-CMP`).converstionRate)
      setMinsToTextRate(transformProducts.find((product) => product.productLegacyId === `BALANCE-TRANSFORM-MINS-TO-TEXT-CMP`).converstionRate)
      setTextToDataRate(transformProducts.find((product) => product.productLegacyId === `BALANCE-TRANSFORM-TEXT-TO-DATA-CMP`).converstionRate)
      setTextToMinsRate(transformProducts.find((product) => product.productLegacyId === `BALANCE-TRANSFORM-TEXT-TO-MINS-CMP`).converstionRate)
    }
    catch (error) {

    }
  }

  function handleBalanceACalculation(slide) {
    return balanceCalculator(transformableBalances, typeA, slide)
  }
  function handleBalanceBCalculation(slide) {
    if(balanceA) {
      const min = transformAtoB(typeA, typeB, balanceA.min, balances)
      const max = transformAtoB(typeA, typeB, balanceA.max, balances)
      return balanceCalculator(transformableBalances, typeB, 0, minMaxLimits(transformAtoB(typeA, typeB, slide), min, max) * getConversionRate(typeA,typeB), getConversionRate(typeA,typeB))
    }
  }

  function handleTabAChange(tab) {

    setTypeA(tab.value)

    // Reset all values to minimum value
    setSlide(balanceA.min)

    handleTextAChange(balanceA.formatValue(balanceA.min))
    handleTextBChange(balanceB.formatValue(balanceB.min))
  }

  function handleTabBChange(tab) {
    setTypeB(tab.value)

    // Reset B to minimum value
  
    handleTextBChange(balanceB.formatValue(transformAtoB(typeA, typeB, slide)))
  }

  function handleSliderChange(value) {
    setSlide(value)
    setTextA(balanceA.formatValue(value))
    setTextB(balanceB.formatValue(transformAtoB(typeA, typeB, value)))
  }

  function handleTextAChange(_text) {
    const text = _text.toUpperCase().trim()

    setTextA(text)
    // Works but sometimes gives incorrect numbers depending on type
    // setTextB(balanceB.formatValue(transformAtoB(typeA, typeB, _text)))
    const value = parseTextToSlideValue(text, typeA)

    if (isNumber(value)) {
      setSlide(value)
      // setTextA(balanceA.formatValue(value))
      setTextB(balanceB.formatValue(transformAtoB(typeA, typeB, value)))
    } else {
      setSlide(0)
    }
  }

  function handleTextBChange(_text) {
    const text = _text.toUpperCase().trim()

    setTextB(text)

    const value = parseTextToSlideValue(text, typeB)

    if (isNumber(value)) {
      const converted = transformAtoB(typeB, typeA, value) / getConversionRate(typeA, typeB)
      const convertedFormated = balanceA.formatValue(converted)
      setSlide(converted)
      setTextA(convertedFormated)
    } else {
      setSlide(0)
    }
  }

  function getUnits(type) {
    if(type === 'TEXT') {
      return content.swap.textsDisplayUnits
    } else if(type === 'MINS') {
      return content.swap.minutesDisplayUnits
    } else if(type === 'DATA') {
      return 'MB'
    }
  }
  
  function getAmount(giftAmount, type) {
    if(type === 'TEXT') {
      return giftAmount
    } else if(type === 'MINS') {
      return giftAmount / 60
    } else if (type === 'DATA') {
      return giftAmount / 1000
    }
  }

  async function handleTransform() {
    setData({ loading: true })
    try {
      const values = { from: typeA, to: typeB, amount: slide }
      //our value is actually in seconds, so multiply by 60
      let transformToAmount = parseTextToValue(textB, typeB)
      let transformFromAmount = parseTextToValue(textA, typeA)
      let displayToAmount = parseTextToSlideValue(textB, typeB)
      let displayFromAmount = parseTextToSlideValue(textA, typeA)
      
      const balanceTransformProduct = balanceTransformProducts.find((product) => product.productLegacyId === `BALANCE-TRANSFORM-${typeA}-TO-${typeB}-CMP`)

      if (!balanceTransformProduct){
        throw new Error(content.swap.notFoundBalance)
      }

      const communicationDisplayValues =  {
        fromDisplayAmount: getAmount(displayFromAmount, typeA),
        fromDisplayUnits: getUnits(typeA),
        toDisplayAmount: getAmount(displayToAmount, typeB),
        toDisplayUnits: getUnits(typeB)
      }

      const extendedData = {balance:{balanceReferenceId:balanceA.id,quantity:transformFromAmount},target:{balanceReferenceId:balanceB.id,quantity:transformToAmount}, communicationDisplayValues}
      const order = await client.checkoutServiceProduct(balanceTransformProduct, extendedData)

      if (order.status === "failed"){
        //order failed, throw an exception to be caught
        throw new Error(content.menu.orderFailed)
      }

      if (!textA.match(/^\d{1,}([MG]B)?$/) || !textB.match(/^\d{1,}([MG]B)?$/)) {
        throw new Error(content.swap.incorrectBalanceInput) 
      }
      
      setData({ loading: false })

      const confirmation = {
        title: content.swap.swapped,
        description: `${communicationDisplayValues.fromDisplayAmount} ${communicationDisplayValues.fromDisplayUnits} ${content.swap.ofYourOwn} ${communicationDisplayValues.toDisplayAmount} ${communicationDisplayValues.toDisplayUnits}`
      }
      await client.loadBalancesAndInvoices(accounts[0])
      navigation.navigate('Confirmation', confirmation)
    } catch (error) {
			console.log(error)
			error.message = 'Swap Failed'
      setData({ loading: false, error })
    }
  }

  function handleTranslation(balance) {
    if (!balance || balance === undefined) {
      return ""
    } else if (balance.name === "Texts") {
      return content.swap.texts;
    } else if (balance.name === "Data") {
      return content.swap.data;
    } else if (balance.name === "Minutes") {
      return content.swap.minutes;
    }
  }

  function handleSuffixTranslation(balance) {
    if (!balance || balance === undefined) {
      return ""
    } else if (balance.suffix === "text(s)") {
      return content.swap.textsSuffix
    } else if (balance.suffix === "minute(s)") {
      return content.swap.minutesSuffix
    } else if (balance.suffix === "of data") {
      return content.swap.dataSuffix
    }
  }
  const keyboardVerticalOffset = 90
  const scrollViewRef = useRef();

  return (
    <KeyboardAvoidingView style={styles.container} behavior='height' keyboardVerticalOffset={keyboardVerticalOffset}>
    <ScrollView
    // ref={scrollViewRef}
    >

      <Space height={24} />

      <View style={{flexDirection: 'row', justifyContent: 'space-around'}}>
        <MoonDial
          size={144}
          name={(balanceA && handleTranslation(balanceA))}
          value={balanceA ? balanceA.value : 0}
          range={balanceA ? balanceA.range : 0}
          formatValue={(balanceA && balanceA.formatValueForDial)}
          formatRange={(balanceA && balanceA.formatRange)}
          style={styles.moondialStyle}
          strokeColour={colors.srGreen + 20}
          mostlyFullColour={colors.srDarkGreen}
          almostEmptyColour={colors.srGreen}
          mostlyFullTextColour={colors.srTextInput}
          almostEmptyTextColour={colors.srTextInput}
          textColour={colors.srTextInput}
					injectedContent={content}
          />

        <View style={{justifyContent: 'center'}}>
          <ChevronRight color={colors.feature} size="28" strokeWidth="3" />
        </View>

        <MoonDial
          size={144}
          name={(balanceB && handleTranslation(balanceB))}
          value={balanceB ? balanceB.value : 0}
          range={balanceB ? balanceB.range : 0}
          formatValue={(balanceB && balanceB.formatValueNoConverstion)}
          formatRange={(balanceB && balanceB.formatRange)}
          style={styles.moondialStyle}
          strokeColour={colors.srGreen + 20}
          mostlyFullColour={colors.srDarkGreen}
          almostEmptyColour={colors.srGreen}
          mostlyFullTextColour={colors.srTextInput}
          almostEmptyTextColour={colors.srTextInput}
          textColour={colors.srTextInput}
          />
      </View>

      <Label>{content.swap.from}</Label>

      <Space height={6} />

      <TabSelector
        value={typeA}
        options={tabs.map(tab => ({
          ...tab,
          disabled: noTransferableBalances ||  tab.value === typeB || hasAvailableBalance(tab.value)
        }))}
        onTabPress={handleTabAChange}
        textStyle={styles.tabTextStyle}
      />

      <Space height={6} />

      <Label>{content.swap.to}</Label>

      <Space height={6} />
 
      <TabSelector
        value={typeB}
        options={tabs.map(tab => ({
          ...tab,
          disabled: noTransferableBalances || tab.value === typeA
          // disabled: noTransferableBalances ? true : (tab.value === typeA) 
        }))}
        onTabPress={handleTabBChange}
        textStyle={styles.tabTextStyle}
      />

      <Space height={12} />

      <Slider
        value={balanceA ? minMaxLimits(slide, balanceA.min, balanceA.max) : 0}
        step={(balanceA && balanceA.step)}
        minimumValue={(balanceA && balanceA.min)}
        maximumValue={(balanceA && balanceA.max)}
        onValueChange={handleSliderChange}
        disabled={noTransferableBalances}
      />

      <Space height={12} />

      {(typeA && typeB) &&
        <View>
          <View style={{flexDirection: 'row'}}>
            <View style={{flex: 1}}>
              <TextInputExtended
                label={(balanceA && handleTranslation(balanceA))}
                value={noTransferableBalances ? 0 : textA}
                onChangeText={handleTextAChange}
                error={!isValid}
                hideError
                disabled={noTransferableBalances}
                style={styles.inputStyle}
                footerStyle={styles.footerStyle}
                selectionColor={colors.common}
                onFocus={() => setTimeout(() => {  
                  scrollViewRef.current.scrollToEnd({ animated: true })
                }, 500)}
                {...testID('TransformAmount')}
              />
            </View>

            <Space width={12} />

            <View style={{flex: 1}}>
              <TextInputExtended
                label={(balanceB && handleTranslation(balanceB))}
                value={noTransferableBalances ? 0 : textB}
                onChangeText={handleTextBChange}
                error={!isValid}
                hideError
                disabled={noTransferableBalances}
                style={styles.inputStyle}
                footerStyle={styles.footerStyle}
                selectionColor={colors.common}
                onFocus={() => setTimeout(() => {  
                  scrollViewRef.current.scrollToEnd({ animated: true })
                }, 500)}
                {...testID('TransformAmountTo')}
                />
            </View>
          </View>

          <Space height={8} />

          <Label style={[!isValid && styles.error]}>{limits}</Label>
        </View>
      }

      <Space height={24} />
      {error && [
        <ErrorMessage key={0}>{error.message}</ErrorMessage>,
        <Space height={8} key={1}/>
      ]}
      <Button
        title={content.menu.swapLabel}
        disabled={!isValid || loading}
        onPress={handleTransform}
        titleStyle={{...typography.extrabold, fontSize: 18, color: colors.white}}
        disabledTitleStyle={{...typography.extrabold, fontSize: 18, color: colors.srDarkGreen + 45}}        
        style={styles.enabled}
        disabledStyle={styles.disabled}
        {...testID('TransformSubmit')}
      />

      <Space height={12} />

      <View style={{alignItems: 'center'}}>
        <Text style={styles.result} {...testID('TransformResult')}>{result}</Text>
      </View>
    </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    // paddingTop: 24,
    // paddingBottom: 24,
    ...adaptive.wrapperPadding,
  },
  description: {
    textAlign: 'center',
    ...typography.lightbold
  },
  secondDescription: {
    textAlign: 'center',
    ...typography.demi
  },
  error: {
    color: colors.error,
  },
  result: {
    ...typography.lightbold
  },
  moondialStyle: {
    ...typography.bold
  },
  enabled: {
    backgroundColor: colors.srDarkGreen
  },
  disabled: {
    backgroundColor: colors.srDarkGreen + 60,
    borderColor: colors.srGreen,
    borderWidth: 1
  },
  tabTextStyle: {
    ...typography.lightbold
  },
  inputStyle: {
    ...typography.lightbold
  },
  footerStyle: {
    color: colors.focus
  },
  moondialTextStyle: {
    ...typography.lightbold,
    color: colors.srTextInput
  }
})


